import { useState, useId, useEffect, useRef, type ReactNode } from 'react';

interface TooltipProps {
  /** Text/elements shown in the popover. */
  content: ReactNode;
  /** The element that triggers the tooltip on hover/focus. */
  children: ReactNode;
  /** Accessible label for the trigger when used standalone. */
  label?: string;
  /** Where the popover appears relative to the trigger. */
  placement?: 'top' | 'bottom';
}

/**
 * Minimal tooltip wrapper. Wraps an arbitrary trigger element and reveals a
 * popover on hover or keyboard focus. No external library.
 */
export function Tooltip({ content, children, label, placement = 'top' }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const show = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };

  const hide = () => {
    // Brief delay so moving the pointer from trigger to popover doesn't close it.
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const positionClass =
    placement === 'bottom'
      ? 'top-full left-0 mt-1'
      : 'bottom-full left-0 mb-1';

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <span aria-describedby={open ? id : undefined} aria-label={label}>
        {children}
      </span>
      {open && (
        <span
          role="tooltip"
          id={id}
          className={`pointer-events-none absolute z-50 w-56 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-surface px-3 py-2 text-xs leading-snug text-[var(--color-text-secondary)] shadow-lg ${positionClass}`}
        >
          {content}
        </span>
      )}
    </span>
  );
}

interface HelpTipProps {
  content: ReactNode;
  label?: string;
  placement?: 'top' | 'bottom';
}

/**
 * A standalone "?" icon button that reveals a tooltip. Convenience wrapper
 * around <Tooltip> for the common "explain this field" pattern.
 */
export function HelpTip({ content, label = 'More information', placement = 'top' }: HelpTipProps) {
  return (
    <Tooltip content={content} label={label} placement={placement}>
      <button
        type="button"
        aria-label={label}
        onClick={(e) => e.preventDefault()}
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-current align-middle text-[var(--color-text-muted)] transition-[var(--transition-fast)] hover:text-[var(--color-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span aria-hidden="true" className="text-[10px] font-bold leading-none">?</span>
      </button>
    </Tooltip>
  );
}
